"use client"

import { useState } from "react"
import Input from "@modules/common/components/input"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import ErrorMessage from "@modules/checkout/components/error-message"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { requestPasswordReset } from "@lib/data/customer"

const ForgotPasswordTemplate = () => {
  const [email, setEmail] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const result = await requestPasswordReset(email)

      if (!result.success) {
        throw new Error(result.error || "Failed to send reset email")
      }

      setSubmitted(true)
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="w-full flex justify-start px-8 py-8">
        <div className="max-w-sm w-full flex flex-col items-center">
          <h1 className="text-large-semi uppercase mb-6">Check your email</h1>
          <p className="text-center text-base-regular text-ui-fg-base mb-8">
            If an account exists with the email <strong>{email}</strong>, we&apos;ve
            sent you a link to reset your password.
          </p>
          <p className="text-center text-small-regular text-ui-fg-subtle mb-4">
            Didn&apos;t receive the email? Check your spam folder or try again.
          </p>
          <LocalizedClientLink
            href="/account"
            className="text-ui-fg-base text-small-regular underline"
          >
            Back to login
          </LocalizedClientLink>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full flex justify-start px-8 py-8">
      <div className="max-w-sm w-full flex flex-col items-center">
        <h1 className="text-large-semi uppercase mb-6">Forgot password</h1>
        <p className="text-center text-base-regular text-ui-fg-base mb-8">
          Enter your email address and we&apos;ll send you a link to reset your
          password.
        </p>
        <form className="w-full" onSubmit={handleSubmit}>
          <div className="flex flex-col w-full gap-y-2">
            <Input
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              title="Enter a valid email address."
              autoComplete="email"
              required
              data-testid="email-input"
            />
          </div>
          <ErrorMessage error={error} data-testid="forgot-password-error" />
          <SubmitButton
            data-testid="submit-button"
            className="w-full mt-6"
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send reset link"}
          </SubmitButton>
        </form>
        <LocalizedClientLink
          href="/account"
          className="text-center text-ui-fg-base text-small-regular mt-6 underline"
        >
          Back to login
        </LocalizedClientLink>
      </div>
    </div>
  )
}

export default ForgotPasswordTemplate
